/**
 * Game action definitions & handlers
 * All state changes go through dispatch(action) only
 * Headless - no GUI
 */

import type { ActionHandler } from '@app-games/core';
import type { GameState, ActiveBoost, GoldenEvent, ConquestCountryState, FactionId, OperationDifficulty } from './state.js';
import type { OperationRace } from './data/evolution.js';
import { createInitialState } from './state.js';
import { getEvoNode } from './data/evolution.js';
import {
  createOperationRun, advanceOperation, canBuyEvolution, blankOperation,
} from './systems/operation.js';
import { getBossDef } from './data/boss.js';
import { EVENT_INTERVAL_MIN, EVENT_INTERVAL_MAX, EVENT_DURATION, AD_BONUS_FAVOR, FACTION_EVENTS, MAX_TOTAL_FAVORABILITY } from './data/factions.js';
import { calcFavorTier, pickRandomEvent } from './systems/faction.js';
import { calcBossHp, calcTapDamage, calcBossReward, calcNextBossDelay, pickRandomBoss } from './systems/boss.js';
import { rollAgentDrop, grantAgent, getEquipSlotCount } from './systems/agents.js';
import { PRODUCERS, getProducerCost } from './data/producers.js';
import { CLICK_UPGRADES, SPECIAL_UPGRADES } from './data/upgrades.js';
import { PRESTIGE_UPGRADES, getPrestigeUpgradeCost, PRESTIGE2_UPGRADES } from './data/prestige.js';
import { getTotalCps, getTapPower, getComboTimeout } from './systems/production.js';
import { calculateGcEarned, canPrestige, calculateCoresEarned, canPrestige2, getStartingIp } from './systems/prestige.js';
import { checkAchievements } from './data/achievements.js';
import { generateDailyMissions, getUtcDateString, getMissionTemplate, ALL_COMPLETE_BONUS_DOCS } from './data/missions.js';
import { COUNTRIES, getFacilityCost } from './data/conquest.js';
import { isCountryUnlocked, getInfiltrationPower } from './systems/conquest.js';
import { getNextPromotableRank } from './data/ranks.js';
import type { PrestigeUpgradeId, Prestige2UpgradeId, InfiltrationStats, InfiltrationRunState } from './state.js';
import { INFIL_EVENTS, TUTORIAL_EVENT_IDS, TUTORIAL_STATS, getInfilChoice, higherCareer } from './data/infiltration.js';
import type { InfilChoice, InfilStatType } from './data/infiltration.js';
import {
  computeSuccessRate, rollSuccess, detectionDelta, clampDetection, isCaught,
  resolveFinalOutcome, choiceAmbition, pickCareer, computeRunReward,
  getInfiltrationStage, pickEventCount, pickEventQueue, tutorialForcedSuccess,
  FAIL_COOLDOWN_MS, STAT_GROWTH_ON_SUCCESS,
} from './systems/infiltration.js';

// Action types
export type GameAction =
  | { type: 'TAP' }
  | { type: 'TICK'; payload: { deltaMs: number } }
  | { type: 'BUY_PRODUCER'; payload: { producerId: string; count?: number } }
  | { type: 'BUY_CLICK_UPGRADE'; payload: { index: number } }
  | { type: 'BUY_SPECIAL_UPGRADE'; payload: { upgradeId: string } }
  | { type: 'PRESTIGE_1' }
  | { type: 'PRESTIGE_2' }
  | { type: 'BUY_PRESTIGE_UPGRADE'; payload: { upgradeId: PrestigeUpgradeId } }
  | { type: 'BUY_PRESTIGE2_UPGRADE'; payload: { upgradeId: Prestige2UpgradeId } }
  | { type: 'CLAIM_OFFLINE_REWARD'; payload: { doubled: boolean } }
  | { type: 'TOUCH_GOLDEN_EVENT' }
  | { type: 'SPAWN_GOLDEN_EVENT' }
  | { type: 'CHECK_ACHIEVEMENTS' }
  | { type: 'AD_WATCHED'; payload: { rewardType: string } }
  | { type: 'USE_CLASSIFIED_DOC'; payload: { itemId: string } }
  | { type: 'ACTIVATE_BOOST'; payload: { boost: ActiveBoost } }
  | { type: 'CLEAN_EXPIRED_BOOSTS' }
  | { type: 'ADD_IP'; payload: { amount: number } }
  | { type: 'ADD_DOCS'; payload: { amount: number } }
  | { type: 'REFRESH_DAILY_MISSIONS' }
  | { type: 'UPDATE_MISSION_PROGRESS'; payload: { missionType: string; amount: number } }
  | { type: 'CLAIM_DAILY_MISSION'; payload: { index: number } }
  | { type: 'CLAIM_ALL_MISSIONS_BONUS' }
  | { type: 'SET_ACTIVE_COUNTRY'; payload: { countryId: string | null } }
  | { type: 'INVEST_CONQUEST'; payload: { countryId: string; amount: number } }
  | { type: 'BUILD_FACILITY'; payload: { countryId: string; facilityIdx: number } }
  | { type: 'UPGRADE_FACILITY'; payload: { countryId: string; facilityIdx: number } }
  | { type: 'UNLOCK_ENDING' }
  | { type: 'PROMOTE_RANK' }
  | { type: 'SPAWN_BOSS' }
  | { type: 'TAP_BOSS' }
  | { type: 'CLAIM_BOSS_REWARD' }
  | { type: 'BOSS_EXPIRED' }
  | { type: 'AD_BOSS_DOUBLE_DAMAGE' }
  | { type: 'AD_BOSS_REVIVE' }
  | { type: 'AD_BOSS_REWARD_DOUBLE' }
  | { type: 'GRANT_AGENT'; payload: { agentId: string } }
  | { type: 'EQUIP_AGENT'; payload: { agentId: string; slotIndex: number } }
  | { type: 'UNEQUIP_AGENT'; payload: { slotIndex: number } }
  | { type: 'SPAWN_FACTION_EVENT' }
  | { type: 'HELP_FACTION_EVENT' }
  | { type: 'IGNORE_FACTION_EVENT' }
  | { type: 'AD_FACTION_EVENT_BONUS'; payload: { factionId: FactionId } }
  | { type: 'START_INFILTRATION' }
  | { type: 'CHOOSE_INFILTRATION_OPTION'; payload: { choiceId: string } }
  | { type: 'ADVANCE_INFILTRATION' }
  | { type: 'RESOLVE_INFILTRATION' }
  | { type: 'CLOSE_INFILTRATION' }
  | { type: 'AD_INFILTRATION_RETRY' }
  | { type: 'START_OPERATION'; payload: { race: OperationRace; difficulty: OperationDifficulty; startRegion: string } }
  | { type: 'OPERATION_TICK' }
  | { type: 'BUY_EVOLUTION'; payload: { nodeId: string } }
  | { type: 'COLLECT_DNA_BUBBLE' }
  | { type: 'RESOLVE_OPERATION' }
  | { type: 'ABANDON_OPERATION' }
  | { type: 'RESET' };

// TAP handler
const handleTap: ActionHandler<GameState, Extract<GameAction, { type: 'TAP' }>> = (state) => {
  const now = Date.now();
  const tapPower = getTapPower(state);
  const comboTimeout = getComboTimeout(state);

  // Update combo
  let newCombo = state.comboCount;
  if (now - state.comboLastTapAt <= comboTimeout) {
    newCombo += 1;
  } else {
    newCombo = 1;
  }
  const newMaxCombo = Math.max(state.maxCombo, newCombo);

  return {
    ...state,
    ip: state.ip + tapPower,
    totalIpEarned: state.totalIpEarned + tapPower,
    totalIpThisRun: state.totalIpThisRun + tapPower,
    tapCount: state.tapCount + 1,
    comboCount: newCombo,
    comboLastTapAt: now,
    maxCombo: newMaxCombo,
  };
};

// TICK handler -- auto-production
const handleTick: ActionHandler<GameState, Extract<GameAction, { type: 'TICK' }>> = (state, action) => {
  const { deltaMs } = action.payload;
  const cps = getTotalCps(state);
  const ipEarned = cps * (deltaMs / 1000);
  if (ipEarned <= 0 && state.activeBoosts.length === 0 && state.prestige2Upgrades.galaxyAutomate === 0) return null;

  const now = Date.now();

  // Auto-click from prestige upgrade
  const autoClickLv = state.prestigeUpgrades.autoClick;
  let autoTapIp = 0;
  if (autoClickLv > 0) {
    const tapsPerSec = autoClickLv * 3;
    const tapPower = getTapPower({ ...state, comboCount: 0 }); // no combo for auto
    autoTapIp = tapPower * tapsPerSec * (deltaMs / 1000);
  }

  const totalEarned = ipEarned + autoTapIp;

  // Galaxy automate: auto-buy cheapest affordable producer each tick
  let newProducers = state.producers;
  let newIp = state.ip + totalEarned;
  if (state.prestige2Upgrades.galaxyAutomate > 0) {
    const discountPct = state.prestigeUpgrades.agentDiscount * 10;
    let bought = true;
    // Keep buying cheapest producer while affordable (cap iterations to prevent freeze)
    let iterations = 0;
    while (bought && iterations < 10) {
      bought = false;
      iterations++;
      let cheapestCost = Infinity;
      let cheapestId = '';
      for (const def of PRODUCERS) {
        const owned = newProducers[def.id]?.count ?? 0;
        const cost = getProducerCost(def.baseCost, owned, discountPct);
        if (cost < cheapestCost && newIp >= cost) {
          cheapestCost = cost;
          cheapestId = def.id;
        }
      }
      if (cheapestId && cheapestCost <= newIp) {
        const owned = newProducers[cheapestId]?.count ?? 0;
        newProducers = { ...newProducers, [cheapestId]: { count: owned + 1 } };
        newIp -= cheapestCost;
        bought = true;
      }
    }
  }

  // Combo decay
  const comboTimeout = getComboTimeout(state);
  let newCombo = state.comboCount;
  if (state.comboCount > 0 && now - state.comboLastTapAt > comboTimeout) {
    newCombo = 0;
  }

  // Clean expired boosts
  const activeBoosts = state.activeBoosts.filter(b => now < b.endsAt);

  // Update play time
  const newPlayTime = state.totalPlayTimeMs + deltaMs;

  // Infiltration progress (active country only)
  let newConquest = state.conquest;
  const activeId = state.conquest.activeCountry;
  if (activeId) {
    const country = COUNTRIES.find(c => c.id === activeId);
    const cs = state.conquest.countries[activeId];
    if (country && cs && !cs.conquered) {
      const infPower = getInfiltrationPower(state);
      const progress = infPower * (deltaMs / 1000);
      if (progress > 0) {
        const newInvested = Math.min(cs.invested + progress, country.conquestCost);
        const newConquered = newInvested >= country.conquestCost;
        const newCountries = {
          ...state.conquest.countries,
          [activeId]: { ...cs, invested: newInvested, conquered: newConquered },
        };

        // quickInfiltration: on conquest, seed next country with bonus
        if (newConquered) {
          const qiLv = state.prestigeUpgrades?.quickInfiltration ?? 0;
          if (qiLv > 0) {
            const nextCountry = COUNTRIES.find(c => c.order === country.order + 1);
            if (nextCountry) {
              const bonusPct = qiLv * 0.1; // 10% per level
              const bonus = nextCountry.conquestCost * bonusPct;
              const existing = newCountries[nextCountry.id];
              if (!existing || !existing.conquered) {
                newCountries[nextCountry.id] = {
                  invested: (existing?.invested ?? 0) + bonus,
                  conquered: false,
                  facilities: existing?.facilities ?? [0, 0, 0],
                };
              }
            }
          }
        }

        newConquest = {
          ...state.conquest,
          countries: newCountries,
          activeCountry: newConquered ? null : activeId,
        };
      }
    }
  }

  return {
    ...state,
    ip: newIp,
    totalIpEarned: state.totalIpEarned + totalEarned,
    totalIpThisRun: state.totalIpThisRun + totalEarned,
    producers: newProducers,
    comboCount: newCombo,
    activeBoosts,
    totalPlayTimeMs: newPlayTime,
    lastOnlineAt: now,
    conquest: newConquest,
  };
};

// BUY_PRODUCER handler
const handleBuyProducer: ActionHandler<GameState, Extract<GameAction, { type: 'BUY_PRODUCER' }>> = (state, action) => {
  const { producerId, count: buyCount = 1 } = action.payload;
  const def = PRODUCERS.find(p => p.id === producerId);
  if (!def) return null;

  const owned = state.producers[producerId]?.count ?? 0;
  const discountPct = state.prestigeUpgrades.agentDiscount * 10;

  // Calculate total cost for buying `buyCount` units
  let totalCost = 0;
  for (let i = 0; i < buyCount; i++) {
    totalCost += getProducerCost(def.baseCost, owned + i, discountPct);
  }

  if (state.ip < totalCost) return null;

  return {
    ...state,
    ip: state.ip - totalCost,
    producers: {
      ...state.producers,
      [producerId]: { count: owned + buyCount },
    },
  };
};

// BUY_CLICK_UPGRADE handler
const handleBuyClickUpgrade: ActionHandler<GameState, Extract<GameAction, { type: 'BUY_CLICK_UPGRADE' }>> = (state, action) => {
  const { index } = action.payload;
  const upg = CLICK_UPGRADES[index];
  if (!upg) return null;
  if (state.clickUpgradesBought.includes(index)) return null;
  // Must buy previous upgrade first (index 0 is always unlocked)
  if (index > 0 && !state.clickUpgradesBought.includes(index - 1)) return null;
  if (state.ip < upg.cost) return null;

  return {
    ...state,
    ip: state.ip - upg.cost,
    clickUpgradesBought: [...state.clickUpgradesBought, index],
  };
};

// BUY_SPECIAL_UPGRADE handler
const handleBuySpecialUpgrade: ActionHandler<GameState, Extract<GameAction, { type: 'BUY_SPECIAL_UPGRADE' }>> = (state, action) => {
  const { upgradeId } = action.payload;
  const upg = SPECIAL_UPGRADES.find(s => s.id === upgradeId);
  if (!upg) return null;
  if (state.specialUpgradesBought.includes(upgradeId)) return null;
  if (state.ip < upg.cost) return null;

  // Check condition
  const cond = upg.condition;
  if (cond.type === 'producers') {
    for (const req of cond.requirements) {
      if ((state.producers[req.producerId]?.count ?? 0) < req.count) return null;
    }
  } else if (cond.type === 'combo') {
    if (state.maxCombo < cond.maxCombo) return null;
  } else if (cond.type === 'playtime') {
    if (state.totalPlayTimeMs < cond.hours * 3600_000) return null;
  } else if (cond.type === 'totalProducers') {
    const total = Object.values(state.producers).reduce((s, p) => s + p.count, 0);
    if (total < cond.count) return null;
  }

  return {
    ...state,
    ip: state.ip - upg.cost,
    specialUpgradesBought: [...state.specialUpgradesBought, upgradeId],
  };
};

// PRESTIGE_1 handler
const handlePrestige1: ActionHandler<GameState, Extract<GameAction, { type: 'PRESTIGE_1' }>> = (state) => {
  if (!canPrestige(state)) return null;

  let gcEarned = calculateGcEarned(state.totalIpThisRun, state);
  if (state.prestigeBoostActive) gcEarned *= 2;
  const startingIp = getStartingIp(state);

  // quickStart: auto-buy first 5 producers (1 each)
  const producers: typeof state.producers = {};
  if (state.prestigeUpgrades.quickStart > 0) {
    for (let i = 0; i < 5 && i < PRODUCERS.length; i++) {
      producers[PRODUCERS[i].id] = { count: 1 };
    }
  }

  return {
    ...state,
    // Reset
    ip: startingIp,
    totalIpThisRun: 0,
    producers,
    clickUpgradesBought: [],
    specialUpgradesBought: [],
    activeBoosts: [],
    prestigeBoostActive: false,
    comboCount: 0,
    comboLastTapAt: 0,
    baseTapPower: 1,
    currentGoldenEvent: null,
    conquest: { activeCountry: null, countries: {}, endingUnlocked: false },
    rank: 'E',
    // Clear current boss fight but keep stats
    boss: {
      ...state.boss,
      current: null,
      nextBossAt: Date.now() + 10 * 60_000 + Math.random() * 10 * 60_000,
    },
    // Keep
    totalIpEarned: state.totalIpEarned,
    gc: state.gc + gcEarned,
    totalGcEarned: state.totalGcEarned + gcEarned,
    prestigeCount: state.prestigeCount + 1,
    faction: state.faction, // faction persists across prestige
    // Preserve prestige upgrades, achievements, stats, docs, p2, etc.
  };
};

// PRESTIGE_2 handler
const handlePrestige2: ActionHandler<GameState, Extract<GameAction, { type: 'PRESTIGE_2' }>> = (state) => {
  if (!canPrestige2(state)) return null;

  const coresEarned = calculateCoresEarned(state.totalGcEarned, state);

  // Deep reset - reset GC, prestige upgrades, everything from prestige 1
  const fresh = createInitialState();

  return {
    ...fresh,
    // Preserved across prestige 2
    cores: state.cores + coresEarned,
    totalCoresEarned: state.totalCoresEarned + coresEarned,
    prestige2Count: state.prestige2Count + 1,
    prestige2Upgrades: state.prestige2Upgrades,
    achievements: state.achievements,
    totalPlayTimeMs: state.totalPlayTimeMs,
    totalAdsWatched: state.totalAdsWatched,
    classifiedDocs: state.classifiedDocs,
    totalDocsUsed: state.totalDocsUsed,
    totalIpEarned: state.totalIpEarned,
    gameStartedAt: state.gameStartedAt,
    sessionStartAt: Date.now(),
    bgmVolume: state.bgmVolume,
    sfxVolume: state.sfxVolume,
    // Agent collection persists across prestige 2
    agentCollection: state.agentCollection,
    // Faction persists across prestige 2
    faction: state.faction,
  };
};

// BUY_PRESTIGE_UPGRADE handler
const handleBuyPrestigeUpgrade: ActionHandler<GameState, Extract<GameAction, { type: 'BUY_PRESTIGE_UPGRADE' }>> = (state, action) => {
  const { upgradeId } = action.payload;
  const def = PRESTIGE_UPGRADES.find(u => u.id === upgradeId);
  if (!def) return null;

  const currentLv = state.prestigeUpgrades[upgradeId];
  if (currentLv >= def.maxLevel) return null;

  const cost = getPrestigeUpgradeCost(def, currentLv);
  if (state.gc < cost) return null;

  return {
    ...state,
    gc: state.gc - cost,
    prestigeUpgrades: {
      ...state.prestigeUpgrades,
      [upgradeId]: currentLv + 1,
    },
  };
};

// BUY_PRESTIGE2_UPGRADE handler
const handleBuyPrestige2Upgrade: ActionHandler<GameState, Extract<GameAction, { type: 'BUY_PRESTIGE2_UPGRADE' }>> = (state, action) => {
  const { upgradeId } = action.payload;
  const def = PRESTIGE2_UPGRADES.find(u => u.id === upgradeId);
  if (!def) return null;

  const currentLv = state.prestige2Upgrades[upgradeId];
  if (currentLv >= def.maxLevel) return null;
  if (state.cores < def.cost) return null;

  return {
    ...state,
    cores: state.cores - def.cost,
    prestige2Upgrades: {
      ...state.prestige2Upgrades,
      [upgradeId]: currentLv + 1,
    },
  };
};

// CLAIM_OFFLINE_REWARD handler
const handleClaimOfflineReward: ActionHandler<GameState, Extract<GameAction, { type: 'CLAIM_OFFLINE_REWARD' }>> = (state, action) => {
  const amount = action.payload.doubled ? state.offlineIpPending * 2 : state.offlineIpPending;
  if (amount <= 0) return null;

  return {
    ...state,
    ip: state.ip + amount,
    totalIpEarned: state.totalIpEarned + amount,
    totalIpThisRun: state.totalIpThisRun + amount,
    offlineIpPending: 0,
  };
};

// TOUCH_GOLDEN_EVENT handler
const handleTouchGoldenEvent: ActionHandler<GameState, Extract<GameAction, { type: 'TOUCH_GOLDEN_EVENT' }>> = (state) => {
  const evt = state.currentGoldenEvent;
  if (!evt) return null;
  if (Date.now() > evt.expiresAt) return null;

  const cps = getTotalCps(state);
  let ipGain = 0;
  let docsGain = 0;
  let newBoosts = [...state.activeBoosts];
  const now = Date.now();

  switch (evt.type) {
    case 'intel_leak':
      ipGain = cps * 3;
      break;
    case 'agent_frenzy':
      newBoosts.push({ type: 'tap5x', multiplier: 5, endsAt: now + 10_000 });
      break;
    case 'money_launder':
      ipGain = state.ip * 0.05;
      break;
    case 'facility_boost':
      newBoosts.push({ type: 'cps3x', multiplier: 3, endsAt: now + 15_000 });
      break;
    case 'supply_drop':
      docsGain = 1;
      break;
  }

  // Golden luck bonus
  const goldenLuckLv = state.prestigeUpgrades.goldenLuck;
  if (goldenLuckLv > 0 && ipGain > 0) {
    ipGain = Math.floor(ipGain * (1 + goldenLuckLv * 0.2));
  }

  // Achievement bonus
  const achEventRewardPct = state.achievements['C03']?.unlocked ? 10 : 0;
  if (achEventRewardPct > 0 && ipGain > 0) {
    ipGain = Math.floor(ipGain * (1 + achEventRewardPct / 100));
  }

  // Schedule next golden event
  let baseInterval = 300_000 + Math.random() * 600_000; // 5-15 min
  // Orbital surveillance: -50%
  if (state.specialUpgradesBought.includes('orbitalSurveillance')) {
    baseInterval *= 0.5;
  }
  // Achievement: S05 -20% frequency
  if (state.achievements['S05']?.unlocked) baseInterval *= 0.8;
  // Achievement: C04 -10%
  if (state.achievements['C04']?.unlocked) baseInterval *= 0.9;

  return {
    ...state,
    ip: state.ip + ipGain,
    totalIpEarned: state.totalIpEarned + ipGain,
    totalIpThisRun: state.totalIpThisRun + ipGain,
    classifiedDocs: state.classifiedDocs + docsGain,
    activeBoosts: newBoosts,
    currentGoldenEvent: null,
    goldenEventsTouched: state.goldenEventsTouched + 1,
    lastGoldenEventAt: now,
    nextGoldenEventAt: now + baseInterval,
  };
};

// SPAWN_GOLDEN_EVENT handler
const handleSpawnGoldenEvent: ActionHandler<GameState, Extract<GameAction, { type: 'SPAWN_GOLDEN_EVENT' }>> = (state) => {
  const now = Date.now();
  if (state.currentGoldenEvent && now < state.currentGoldenEvent.expiresAt) return null;
  if (now < state.nextGoldenEventAt) return null;

  // Randomly pick event type
  const rand = Math.random();
  let eventType: GoldenEvent['type'];
  if (rand < 0.30) eventType = 'intel_leak';
  else if (rand < 0.55) eventType = 'agent_frenzy';
  else if (rand < 0.80) eventType = 'money_launder';
  else if (rand < 0.95) eventType = 'facility_boost';
  else eventType = 'supply_drop';

  const evt: GoldenEvent = {
    id: `ge-${now}`,
    type: eventType,
    appearedAt: now,
    expiresAt: now + 7000, // 7 seconds
  };

  return {
    ...state,
    currentGoldenEvent: evt,
  };
};

// CHECK_ACHIEVEMENTS handler
const handleCheckAchievements: ActionHandler<GameState, Extract<GameAction, { type: 'CHECK_ACHIEVEMENTS' }>> = (state) => {
  const newlyUnlocked = checkAchievements(state);
  if (newlyUnlocked.length === 0) return null;

  const newAchievements = { ...state.achievements };
  const now = Date.now();
  for (const id of newlyUnlocked) {
    newAchievements[id] = { unlocked: true, unlockedAt: now };
  }

  return {
    ...state,
    achievements: newAchievements,
  };
};

// AD_WATCHED handler
const handleAdWatched: ActionHandler<GameState, Extract<GameAction, { type: 'AD_WATCHED' }>> = (state) => {
  return {
    ...state,
    totalAdsWatched: state.totalAdsWatched + 1,
  };
};

// USE_CLASSIFIED_DOC handler
const handleUseDoc: ActionHandler<GameState, Extract<GameAction, { type: 'USE_CLASSIFIED_DOC' }>> = (state, action) => {
  const { itemId } = action.payload;
  const now = Date.now();
  const cps = getTotalCps(state);

  let cost = 0;
  let ipGain = 0;
  let newBoosts = [...state.activeBoosts];

  switch (itemId) {
    case 'ip_boost_30s': cost = 3; newBoosts.push({ type: 'cps2x', multiplier: 2, endsAt: now + 30_000 }); break;
    case 'mega_tap_30s': cost = 3; newBoosts.push({ type: 'tap3x', multiplier: 3, endsAt: now + 30_000 }); break;
    case 'auto_click_30s': cost = 3; newBoosts.push({ type: 'autoClick', multiplier: 10, endsAt: now + 30_000 }); break;
    case 'instant_golden': cost = 5; break; // handled in UI
    case 'timeskip_30s': cost = 5; ipGain = cps * 7200; break;
    case 'prestige_boost':
      if (state.prestigeBoostActive) return null; // already purchased, block re-buy
      cost = 8;
      break;
    default: return null;
  }

  if (state.classifiedDocs < cost) return null;

  return {
    ...state,
    classifiedDocs: state.classifiedDocs - cost,
    totalDocsUsed: state.totalDocsUsed + cost,
    ip: state.ip + ipGain,
    totalIpEarned: state.totalIpEarned + ipGain,
    totalIpThisRun: state.totalIpThisRun + ipGain,
    activeBoosts: newBoosts,
    prestigeBoostActive: itemId === 'prestige_boost' ? true : state.prestigeBoostActive,
  };
};

// ACTIVATE_BOOST handler
const handleActivateBoost: ActionHandler<GameState, Extract<GameAction, { type: 'ACTIVATE_BOOST' }>> = (state, action) => {
  return {
    ...state,
    activeBoosts: [...state.activeBoosts, action.payload.boost],
  };
};

// CLEAN_EXPIRED_BOOSTS handler
const handleCleanBoosts: ActionHandler<GameState, Extract<GameAction, { type: 'CLEAN_EXPIRED_BOOSTS' }>> = (state) => {
  const now = Date.now();
  const filtered = state.activeBoosts.filter(b => now < b.endsAt);
  if (filtered.length === state.activeBoosts.length) return null;
  return { ...state, activeBoosts: filtered };
};

// ADD_IP handler (for testing/debug)
const handleAddIp: ActionHandler<GameState, Extract<GameAction, { type: 'ADD_IP' }>> = (state, action) => {
  return {
    ...state,
    ip: state.ip + action.payload.amount,
    totalIpEarned: state.totalIpEarned + action.payload.amount,
    totalIpThisRun: state.totalIpThisRun + action.payload.amount,
  };
};

// ADD_DOCS handler
const handleAddDocs: ActionHandler<GameState, Extract<GameAction, { type: 'ADD_DOCS' }>> = (state, action) => {
  return { ...state, classifiedDocs: state.classifiedDocs + action.payload.amount };
};

// REFRESH_DAILY_MISSIONS handler - checks date and regenerates if needed
const handleRefreshDailyMissions: ActionHandler<GameState, Extract<GameAction, { type: 'REFRESH_DAILY_MISSIONS' }>> = (state) => {
  const today = getUtcDateString();
  if (state.dailyMissionDate === today && state.dailyMissions.length > 0) return null;

  const missions = generateDailyMissions(today);
  return {
    ...state,
    dailyMissions: missions,
    dailyMissionDate: today,
    dailyMissionAllClaimed: false,
  };
};

// UPDATE_MISSION_PROGRESS handler - updates progress for matching missions
const handleUpdateMissionProgress: ActionHandler<GameState, Extract<GameAction, { type: 'UPDATE_MISSION_PROGRESS' }>> = (state, action) => {
  const { missionType, amount } = action.payload;
  if (state.dailyMissions.length === 0) return null;

  let changed = false;
  const newMissions = state.dailyMissions.map(m => {
    if (m.claimed) return m;
    const template = getMissionTemplate(m.templateId);
    if (!template || template.type !== missionType) return m;
    if (m.progress >= m.target) return m;

    changed = true;
    return { ...m, progress: Math.min(m.progress + amount, m.target) };
  });

  if (!changed) return null;
  return { ...state, dailyMissions: newMissions };
};

// CLAIM_DAILY_MISSION handler
const handleClaimDailyMission: ActionHandler<GameState, Extract<GameAction, { type: 'CLAIM_DAILY_MISSION' }>> = (state, action) => {
  const { index } = action.payload;
  const mission = state.dailyMissions[index];
  if (!mission || mission.claimed || mission.progress < mission.target) return null;

  const template = getMissionTemplate(mission.templateId);
  if (!template) return null;

  const newMissions = [...state.dailyMissions];
  newMissions[index] = { ...mission, claimed: true };

  let newBoosts = [...state.activeBoosts];
  const now = Date.now();
  if (template.reward.boostSeconds && template.reward.boostType) {
    newBoosts.push({
      type: template.reward.boostType as ActiveBoost['type'],
      multiplier: template.reward.boostType === 'cps2x' ? 2 : template.reward.boostType === 'cps3x' ? 3 : 5,
      endsAt: now + template.reward.boostSeconds * 1000,
    });
  }

  return {
    ...state,
    dailyMissions: newMissions,
    classifiedDocs: state.classifiedDocs + template.reward.docs,
    activeBoosts: newBoosts,
  };
};

// CLAIM_ALL_MISSIONS_BONUS handler - bonus for completing all daily missions
const handleClaimAllMissionsBonus: ActionHandler<GameState, Extract<GameAction, { type: 'CLAIM_ALL_MISSIONS_BONUS' }>> = (state) => {
  if (state.dailyMissionAllClaimed) return null;
  const allClaimed = state.dailyMissions.length > 0 && state.dailyMissions.every(m => m.claimed);
  if (!allClaimed) return null;

  return {
    ...state,
    classifiedDocs: state.classifiedDocs + ALL_COMPLETE_BONUS_DOCS,
    dailyMissionAllClaimed: true,
  };
};

// SET_ACTIVE_COUNTRY handler
const handleSetActiveCountry: ActionHandler<GameState, Extract<GameAction, { type: 'SET_ACTIVE_COUNTRY' }>> = (state, action) => {
  const { countryId } = action.payload;
  if (countryId !== null) {
    if (!isCountryUnlocked(countryId, state)) return null;
    const cs = state.conquest.countries[countryId];
    if (cs?.conquered) return null;
  }
  // Ensure country entry exists
  let newCountries = state.conquest.countries;
  if (countryId && !newCountries[countryId]) {
    newCountries = {
      ...newCountries,
      [countryId]: { invested: 0, conquered: false, facilities: [0, 0, 0] },
    };
  }
  return {
    ...state,
    conquest: { ...state.conquest, activeCountry: countryId, countries: newCountries },
  };
};

// INVEST_CONQUEST handler
const handleInvestConquest: ActionHandler<GameState, Extract<GameAction, { type: 'INVEST_CONQUEST' }>> = (state, action) => {
  const { countryId, amount } = action.payload;
  if (amount <= 0 || state.ip < amount) return null;
  const country = COUNTRIES.find(c => c.id === countryId);
  if (!country) return null;
  if (!isCountryUnlocked(countryId, state)) return null;

  const cs: ConquestCountryState = state.conquest.countries[countryId] || { invested: 0, conquered: false, facilities: [0, 0, 0] };
  if (cs.conquered) return null;

  const newInvested = Math.min(cs.invested + amount, country.conquestCost);
  const newConquered = newInvested >= country.conquestCost;

  return {
    ...state,
    ip: state.ip - amount,
    conquest: {
      ...state.conquest,
      countries: {
        ...state.conquest.countries,
        [countryId]: { ...cs, invested: newInvested, conquered: newConquered },
      },
    },
  };
};

// BUILD_FACILITY handler
const handleBuildFacility: ActionHandler<GameState, Extract<GameAction, { type: 'BUILD_FACILITY' }>> = (state, action) => {
  const { countryId, facilityIdx } = action.payload;
  const country = COUNTRIES.find(c => c.id === countryId);
  if (!country) return null;
  const cs = state.conquest.countries[countryId];
  if (!cs?.conquered) return null;
  const fac = country.facilities[facilityIdx];
  if (!fac) return null;
  if (cs.facilities[facilityIdx] > 0) return null;
  if (state.ip < fac.baseCost) return null;

  const newFacilities: [number, number, number] = [...cs.facilities] as [number, number, number];
  newFacilities[facilityIdx] = 1;

  return {
    ...state,
    ip: state.ip - fac.baseCost,
    conquest: {
      ...state.conquest,
      countries: {
        ...state.conquest.countries,
        [countryId]: { ...cs, facilities: newFacilities },
      },
    },
  };
};

// UPGRADE_FACILITY handler
const handleUpgradeFacility: ActionHandler<GameState, Extract<GameAction, { type: 'UPGRADE_FACILITY' }>> = (state, action) => {
  const { countryId, facilityIdx } = action.payload;
  const country = COUNTRIES.find(c => c.id === countryId);
  if (!country) return null;
  const cs = state.conquest.countries[countryId];
  if (!cs?.conquered) return null;
  const fac = country.facilities[facilityIdx];
  if (!fac) return null;
  const currentLv = cs.facilities[facilityIdx];
  if (currentLv < 1) return null;

  const cost = getFacilityCost(fac.baseCost, currentLv);
  if (state.ip < cost) return null;

  const newFacilities: [number, number, number] = [...cs.facilities] as [number, number, number];
  newFacilities[facilityIdx] = currentLv + 1;

  return {
    ...state,
    ip: state.ip - cost,
    conquest: {
      ...state.conquest,
      countries: {
        ...state.conquest.countries,
        [countryId]: { ...cs, facilities: newFacilities },
      },
    },
  };
};

// UNLOCK_ENDING handler
const handleUnlockEnding: ActionHandler<GameState, Extract<GameAction, { type: 'UNLOCK_ENDING' }>> = (state) => {
  if (state.conquest.endingUnlocked) return null;
  return {
    ...state,
    conquest: { ...state.conquest, endingUnlocked: true },
  };
};

// PROMOTE_RANK handler
const handlePromoteRank: ActionHandler<GameState, Extract<GameAction, { type: 'PROMOTE_RANK' }>> = (state) => {
  const next = getNextPromotableRank(state.rank ?? 'E', state.totalIpEarned, state.prestigeCount);
  if (!next) return null;
  if (state.ip < next.promotionCost) return null;
  return {
    ...state,
    ip: state.ip - next.promotionCost,
    rank: next.grade,
  };
};

// SPAWN_BOSS handler - spawns a random available boss
const handleSpawnBoss: ActionHandler<GameState, Extract<GameAction, { type: 'SPAWN_BOSS' }>> = (state) => {
  if (state.boss.current) return null; // already fighting a boss
  const now = Date.now();
  if (now < state.boss.nextBossAt) return null;

  const bossDef = pickRandomBoss(state);
  if (!bossDef) return null;

  const cps = getTotalCps(state);
  const maxHp = calcBossHp(cps, bossDef.baseHpMultiplier);

  return {
    ...state,
    boss: {
      ...state.boss,
      current: {
        bossId: bossDef.id,
        spawnedAt: now,
        expiresAt: now + bossDef.timeLimit,
        maxHp,
        currentHp: maxHp,
        phase: 1,
        rewardClaimed: false,
        doubleDamageUntil: 0,
        reviveUsed: false,
      },
    },
  };
};

// TAP_BOSS handler - deal tap damage to boss
const handleTapBoss: ActionHandler<GameState, Extract<GameAction, { type: 'TAP_BOSS' }>> = (state) => {
  const boss = state.boss.current;
  if (!boss || boss.currentHp <= 0) return null;
  const now = Date.now();
  if (now > boss.expiresAt) return null;

  const tapPower = getTapPower(state);
  let damage = calcTapDamage(tapPower);

  // Double damage from ad boost
  if (boss.doubleDamageUntil > 0 && now < boss.doubleDamageUntil) {
    damage *= 2;
  }

  const newHp = Math.max(0, boss.currentHp - damage);
  const newPhase: 1 | 2 = newHp <= boss.maxHp * 0.5 ? 2 : boss.phase;

  return {
    ...state,
    boss: {
      ...state.boss,
      current: {
        ...boss,
        currentHp: newHp,
        phase: newPhase,
      },
    },
  };
};

// CLAIM_BOSS_REWARD handler - collect reward after killing boss
const handleClaimBossReward: ActionHandler<GameState, Extract<GameAction, { type: 'CLAIM_BOSS_REWARD' }>> = (state) => {
  const boss = state.boss.current;
  if (!boss || boss.currentHp > 0 || boss.rewardClaimed) return null;

  const def = getBossDef(boss.bossId);
  if (!def) return null;

  const cps = getTotalCps(state);
  const reward = calcBossReward(boss, def, cps, state.boss.bossKillStreak);
  const now = Date.now();
  const nextDelay = calcNextBossDelay(state);

  let newBoosts = [...state.activeBoosts];
  if (reward.boostSeconds > 0) {
    newBoosts.push({
      type: 'cps3x',
      multiplier: 3,
      endsAt: now + reward.boostSeconds * 1000,
    });
  }

  // Agent drop from boss kill
  const droppedAgentId = rollAgentDrop(boss.bossId);
  let newAgentCollection = state.agentCollection;
  if (droppedAgentId) {
    newAgentCollection = {
      ...newAgentCollection,
      collected: grantAgent(newAgentCollection.collected, droppedAgentId),
      totalCollected: newAgentCollection.totalCollected + 1,
    };
  }

  return {
    ...state,
    ip: state.ip + reward.ip,
    totalIpEarned: state.totalIpEarned + reward.ip,
    totalIpThisRun: state.totalIpThisRun + reward.ip,
    classifiedDocs: state.classifiedDocs + reward.docs,
    gc: state.gc + reward.gc,
    totalGcEarned: state.totalGcEarned + reward.gc,
    activeBoosts: newBoosts,
    agentCollection: newAgentCollection,
    boss: {
      ...state.boss,
      current: { ...boss, rewardClaimed: true },
      nextBossAt: now + nextDelay,
      totalBossesDefeated: state.boss.totalBossesDefeated + 1,
      bossKillStreak: state.boss.bossKillStreak + 1,
    },
  };
};

// BOSS_EXPIRED handler - boss escaped (time ran out)
const handleBossExpired: ActionHandler<GameState, Extract<GameAction, { type: 'BOSS_EXPIRED' }>> = (state) => {
  const boss = state.boss.current;
  if (!boss || boss.currentHp <= 0) return null;

  const now = Date.now();
  const nextDelay = calcNextBossDelay(state);

  return {
    ...state,
    boss: {
      ...state.boss,
      current: null,
      nextBossAt: now + nextDelay,
      totalBossesExpired: state.boss.totalBossesExpired + 1,
      bossKillStreak: 0, // streak resets on escape
    },
  };
};

// AD_BOSS_DOUBLE_DAMAGE handler - ad: 2x damage for 30 seconds
const handleAdBossDoubleDamage: ActionHandler<GameState, Extract<GameAction, { type: 'AD_BOSS_DOUBLE_DAMAGE' }>> = (state) => {
  const boss = state.boss.current;
  if (!boss || boss.currentHp <= 0) return null;

  const now = Date.now();
  return {
    ...state,
    totalAdsWatched: state.totalAdsWatched + 1,
    boss: {
      ...state.boss,
      current: {
        ...boss,
        doubleDamageUntil: now + 30_000,
      },
    },
  };
};

// AD_BOSS_REVIVE handler - ad: revive boss fight with reduced HP + extra time
const handleAdBossRevive: ActionHandler<GameState, Extract<GameAction, { type: 'AD_BOSS_REVIVE' }>> = (state) => {
  const boss = state.boss.current;
  if (!boss || boss.reviveUsed) return null;
  // Only available when boss HP > 0 (escaped, not killed)
  if (boss.currentHp <= 0) return null;

  const now = Date.now();
  return {
    ...state,
    totalAdsWatched: state.totalAdsWatched + 1,
    boss: {
      ...state.boss,
      current: {
        ...boss,
        currentHp: boss.currentHp * 0.3, // reduce remaining HP to 30%
        expiresAt: now + 20_000,          // +20 seconds
        reviveUsed: true,
      },
    },
  };
};

// AD_BOSS_REWARD_DOUBLE handler - ad: double boss kill reward
const handleAdBossRewardDouble: ActionHandler<GameState, Extract<GameAction, { type: 'AD_BOSS_REWARD_DOUBLE' }>> = (state) => {
  const boss = state.boss.current;
  if (!boss || boss.currentHp > 0 || boss.rewardClaimed) return null;

  const def = getBossDef(boss.bossId);
  if (!def) return null;

  const cps = getTotalCps(state);
  const reward = calcBossReward(boss, def, cps, state.boss.bossKillStreak);
  const now = Date.now();
  const nextDelay = calcNextBossDelay(state);

  // Double all rewards
  let newBoosts = [...state.activeBoosts];
  if (reward.boostSeconds > 0) {
    newBoosts.push({
      type: 'cps3x',
      multiplier: 3,
      endsAt: now + reward.boostSeconds * 2 * 1000,
    });
  }

  return {
    ...state,
    ip: state.ip + reward.ip * 2,
    totalIpEarned: state.totalIpEarned + reward.ip * 2,
    totalIpThisRun: state.totalIpThisRun + reward.ip * 2,
    classifiedDocs: state.classifiedDocs + reward.docs * 2,
    gc: state.gc + reward.gc * 2,
    totalGcEarned: state.totalGcEarned + reward.gc * 2,
    activeBoosts: newBoosts,
    totalAdsWatched: state.totalAdsWatched + 1,
    boss: {
      ...state.boss,
      current: { ...boss, rewardClaimed: true },
      nextBossAt: now + nextDelay,
      totalBossesDefeated: state.boss.totalBossesDefeated + 1,
      bossKillStreak: state.boss.bossKillStreak + 1,
    },
  };
};

// GRANT_AGENT handler - add an agent to collection
const handleGrantAgent: ActionHandler<GameState, Extract<GameAction, { type: 'GRANT_AGENT' }>> = (state, action) => {
  const { agentId } = action.payload;
  return {
    ...state,
    agentCollection: {
      ...state.agentCollection,
      collected: grantAgent(state.agentCollection.collected, agentId),
      totalCollected: state.agentCollection.totalCollected + 1,
    },
  };
};

// EQUIP_AGENT handler - equip an agent to a slot
const handleEquipAgent: ActionHandler<GameState, Extract<GameAction, { type: 'EQUIP_AGENT' }>> = (state, action) => {
  const { agentId, slotIndex } = action.payload;
  const ac = state.agentCollection;

  // Must own the agent
  if (!ac.collected[agentId]) return null;

  // Slot must be valid
  const maxSlots = getEquipSlotCount(state);
  if (slotIndex < 0 || slotIndex >= maxSlots) return null;

  // Agent must not already be equipped in another slot
  if (ac.equipped.includes(agentId)) return null;

  const newEquipped = [...ac.equipped];
  // Expand slots if needed
  while (newEquipped.length < maxSlots) newEquipped.push(null);
  newEquipped[slotIndex] = agentId;

  return {
    ...state,
    agentCollection: { ...ac, equipped: newEquipped },
  };
};

// UNEQUIP_AGENT handler - remove an agent from a slot
const handleUnequipAgent: ActionHandler<GameState, Extract<GameAction, { type: 'UNEQUIP_AGENT' }>> = (state, action) => {
  const { slotIndex } = action.payload;
  const ac = state.agentCollection;

  if (slotIndex < 0 || slotIndex >= ac.equipped.length) return null;
  if (ac.equipped[slotIndex] === null) return null;

  const newEquipped = [...ac.equipped];
  newEquipped[slotIndex] = null;

  return {
    ...state,
    agentCollection: { ...ac, equipped: newEquipped },
  };
};

// SPAWN_FACTION_EVENT handler
const handleSpawnFactionEvent: ActionHandler<GameState, Extract<GameAction, { type: 'SPAWN_FACTION_EVENT' }>> = (state) => {
  if (state.faction.currentEvent) return null;

  const evt = pickRandomEvent();
  const now = Date.now();
  const nextDelay = EVENT_INTERVAL_MIN + Math.random() * (EVENT_INTERVAL_MAX - EVENT_INTERVAL_MIN);

  return {
    ...state,
    faction: {
      ...state.faction,
      currentEvent: {
        eventId: evt.id,
        factionId: evt.factionId,
        targetFactionId: evt.targetFactionId,
        expiresAt: now + EVENT_DURATION,
      },
      nextEventAt: now + nextDelay,
    },
  };
};

// HELP_FACTION_EVENT handler
const handleHelpFactionEvent: ActionHandler<GameState, Extract<GameAction, { type: 'HELP_FACTION_EVENT' }>> = (state) => {
  const ev = state.faction.currentEvent;
  if (!ev) return null;

  const evtDef = FACTION_EVENTS.find(e => e.id === ev.eventId);
  if (!evtDef) return null;

  const helpRel = state.faction.relations[ev.factionId];
  const targetRel = state.faction.relations[ev.targetFactionId];
  if (!helpRel || !targetRel) return null;

  const newTargetFavor = Math.max(0, targetRel.favorability - evtDef.helpFavorLoss);
  // Cap: total favorability across all factions cannot exceed MAX_TOTAL_FAVORABILITY
  const otherFactions = (['illuminati', 'grey', 'mib_resistance'] as const).filter(f => f !== ev.factionId && f !== ev.targetFactionId);
  const otherTotal = otherFactions.reduce((sum, f) => sum + state.faction.relations[f].favorability, 0);
  const maxAllowed = MAX_TOTAL_FAVORABILITY - otherTotal - newTargetFavor;
  const newHelpFavor = Math.min(helpRel.favorability + evtDef.helpFavorGain, Math.max(0, maxAllowed));

  // IP reward: CPS * multiplier
  const cps = getTotalCps(state);
  const ipReward = Math.floor(cps * evtDef.ipRewardMultiplier);

  return {
    ...state,
    ip: state.ip + ipReward,
    totalIpEarned: state.totalIpEarned + ipReward,
    totalIpThisRun: state.totalIpThisRun + ipReward,
    faction: {
      ...state.faction,
      relations: {
        ...state.faction.relations,
        [ev.factionId]: { ...helpRel, favorability: newHelpFavor, tier: calcFavorTier(newHelpFavor) },
        [ev.targetFactionId]: { ...targetRel, favorability: newTargetFavor, tier: calcFavorTier(newTargetFavor) },
      },
      currentEvent: null,
      totalEventsHelped: state.faction.totalEventsHelped + 1,
    },
  };
};

// IGNORE_FACTION_EVENT handler
const handleIgnoreFactionEvent: ActionHandler<GameState, Extract<GameAction, { type: 'IGNORE_FACTION_EVENT' }>> = (state) => {
  if (!state.faction.currentEvent) return null;

  return {
    ...state,
    faction: {
      ...state.faction,
      currentEvent: null,
      totalEventsIgnored: state.faction.totalEventsIgnored + 1,
    },
  };
};

// AD_FACTION_EVENT_BONUS handler
const handleAdFactionEventBonus: ActionHandler<GameState, Extract<GameAction, { type: 'AD_FACTION_EVENT_BONUS' }>> = (state, action) => {
  const { factionId } = action.payload;
  const rel = state.faction.relations[factionId];
  if (!rel) return null;

  // Cap: total favorability across all factions cannot exceed MAX_TOTAL_FAVORABILITY
  const adOtherFactions = (['illuminati', 'grey', 'mib_resistance'] as const).filter(f => f !== factionId);
  const adOtherTotal = adOtherFactions.reduce((sum, f) => sum + state.faction.relations[f].favorability, 0);
  const adMaxAllowed = MAX_TOTAL_FAVORABILITY - adOtherTotal;
  const newFavor = Math.min(rel.favorability + AD_BONUS_FAVOR, Math.max(0, adMaxAllowed));

  return {
    ...state,
    totalAdsWatched: state.totalAdsWatched + 1,
    faction: {
      ...state.faction,
      relations: {
        ...state.faction.relations,
        [factionId]: { ...rel, favorability: newFavor, tier: calcFavorTier(newFavor) },
      },
    },
  };
};

// === Infiltration Run ===

/** Stat value used by a choice ('none' choices don't read a stat). */
function statForChoice(stats: InfiltrationStats, statType: InfilStatType): number {
  return statType === 'none' ? 0 : stats[statType];
}

/** Resolve one choice: forced outcome in tutorial, otherwise a real roll. */
function resolveInfilChoice(run: InfiltrationRunState, choice: InfilChoice): { success: boolean; roll: number } {
  if (run.tutorial) return { success: tutorialForcedSuccess(choice), roll: -1 };
  if (choice.statType === 'none') return { success: true, roll: -1 };
  const rate = computeSuccessRate(choice, statForChoice(run.agentStats, choice.statType));
  const roll = Math.random() * 100;
  return { success: rollSuccess(rate, roll), roll: Math.round(roll) };
}

/** Grow every base stat after a successful run (progression). */
function growStats(s: InfiltrationStats): InfiltrationStats {
  const g = STAT_GROWTH_ON_SUCCESS;
  return {
    disguise: Math.min(99, s.disguise + g),
    combat: Math.min(99, s.combat + g),
    hacking: Math.min(99, s.hacking + g),
    charisma: Math.min(99, s.charisma + g),
  };
}

/** Finalize a run as a failure (caught mid-run or failed extraction). */
function finalizeInfilFail(state: GameState, run: InfiltrationRunState, now: number): GameState {
  const inf = state.infiltration;
  const finishedRun: InfiltrationRunState = {
    ...run, finished: true, active: false, finalOutcome: 'fail',
    earnedCareer: null, rewardIp: 0, rewardDocs: 0,
  };
  return {
    ...state,
    infiltration: {
      ...inf, run: finishedRun,
      tutorialDone: inf.tutorialDone || run.tutorial,
      cooldownUntil: now + FAIL_COOLDOWN_MS,
    },
  };
}

/** Finalize a run as a success: grant rewards, career, stat growth. */
function finalizeInfilSuccess(state: GameState, run: InfiltrationRunState): GameState {
  const inf = state.infiltration;
  const rewardIp = computeRunReward(run.accumulatedIp, run.successfulChoices, getTotalCps(state));
  const rewardDocs = run.accumulatedDocs;
  const career = pickCareer(run.ambition, true);
  const finishedRun: InfiltrationRunState = {
    ...run, finished: true, active: false, finalOutcome: 'success',
    earnedCareer: career, rewardIp, rewardDocs,
  };
  return {
    ...state,
    ip: state.ip + rewardIp,
    totalIpEarned: state.totalIpEarned + rewardIp,
    totalIpThisRun: state.totalIpThisRun + rewardIp,
    classifiedDocs: state.classifiedDocs + rewardDocs,
    infiltration: {
      ...inf, run: finishedRun,
      baseStats: growStats(inf.baseStats),
      tutorialDone: inf.tutorialDone || run.tutorial,
      totalSuccess: inf.totalSuccess + 1,
      highestCareer: higherCareer(inf.highestCareer, career),
      cooldownUntil: 0,
    },
  };
}

// START_INFILTRATION handler
const handleStartInfiltration: ActionHandler<GameState, Extract<GameAction, { type: 'START_INFILTRATION' }>> = (state) => {
  const inf = state.infiltration;
  if (inf.run && !inf.run.finished) return null;      // a run is in progress
  if (Date.now() < inf.cooldownUntil) return null;    // on cooldown
  const tutorial = !inf.tutorialDone;
  const stage = getInfiltrationStage(inf.totalSuccess);
  const eventQueue = tutorial
    ? TUTORIAL_EVENT_IDS.slice()
    : pickEventQueue(INFIL_EVENTS, pickEventCount(), stage);
  const agentStats: InfiltrationStats = tutorial ? { ...TUTORIAL_STATS } : { ...inf.baseStats };
  const run: InfiltrationRunState = {
    active: true, agentStats, eventQueue, currentIndex: 0, detection: 0, ambition: 0,
    successfulChoices: 0, accumulatedIp: 0, accumulatedDocs: 0, lastChoiceResult: null,
    finished: false, finalOutcome: null, earnedCareer: null, rewardIp: 0, rewardDocs: 0,
    retryUsed: false, tutorial,
  };
  return { ...state, infiltration: { ...inf, run, totalRuns: inf.totalRuns + 1 } };
};

// CHOOSE_INFILTRATION_OPTION handler
const handleChooseInfiltration: ActionHandler<GameState, Extract<GameAction, { type: 'CHOOSE_INFILTRATION_OPTION' }>> = (state, action) => {
  const inf = state.infiltration;
  const run = inf.run;
  if (!run || !run.active || run.finished || run.lastChoiceResult) return null;
  const eventId = run.eventQueue[run.currentIndex];
  const choice = getInfilChoice(eventId, action.payload.choiceId);
  if (!choice) return null;

  const { success, roll } = resolveInfilChoice(run, choice);
  const detection = clampDetection(run.detection + detectionDelta(choice, success));
  const newRun: InfiltrationRunState = {
    ...run,
    detection,
    ambition: run.ambition + (success ? choiceAmbition(choice) : 0),
    successfulChoices: run.successfulChoices + (success ? 1 : 0),
    accumulatedIp: run.accumulatedIp + (success ? choice.rewardIp : 0),
    accumulatedDocs: run.accumulatedDocs + (success ? choice.rewardDocs : 0),
    lastChoiceResult: { choiceId: choice.id, success, roll },
  };
  if (isCaught(detection)) return finalizeInfilFail(state, newRun, Date.now());
  return { ...state, infiltration: { ...inf, run: newRun } };
};

// ADVANCE_INFILTRATION handler — clear reveal, move to next event
const handleAdvanceInfiltration: ActionHandler<GameState, Extract<GameAction, { type: 'ADVANCE_INFILTRATION' }>> = (state) => {
  const inf = state.infiltration;
  const run = inf.run;
  if (!run || !run.active || run.finished || !run.lastChoiceResult) return null;
  const newRun: InfiltrationRunState = {
    ...run, currentIndex: run.currentIndex + 1, lastChoiceResult: null,
  };
  return { ...state, infiltration: { ...inf, run: newRun } };
};

// RESOLVE_INFILTRATION handler — final extraction judgement
const handleResolveInfiltration: ActionHandler<GameState, Extract<GameAction, { type: 'RESOLVE_INFILTRATION' }>> = (state) => {
  const run = state.infiltration.run;
  if (!run || !run.active || run.finished || run.lastChoiceResult) return null;
  if (run.currentIndex < run.eventQueue.length) return null; // events remain
  const roll = Math.random() * 100;
  const outcome = run.tutorial ? 'success' : resolveFinalOutcome(run.detection, roll);
  return outcome === 'success' ? finalizeInfilSuccess(state, run) : finalizeInfilFail(state, run, Date.now());
};

// AD_INFILTRATION_RETRY handler — opt-in ad grants one second chance
const handleAdInfiltrationRetry: ActionHandler<GameState, Extract<GameAction, { type: 'AD_INFILTRATION_RETRY' }>> = (state) => {
  const inf = state.infiltration;
  const run = inf.run;
  if (!run || !run.finished || run.finalOutcome !== 'fail' || run.retryUsed) return null;
  const newRun: InfiltrationRunState = {
    ...run,
    detection: clampDetection(Math.min(run.detection, 55)),
    finished: false, active: true, finalOutcome: null, earnedCareer: null,
    lastChoiceResult: null, retryUsed: true, currentIndex: run.eventQueue.length,
  };
  return {
    ...state,
    totalAdsWatched: state.totalAdsWatched + 1,
    infiltration: { ...inf, run: newRun, cooldownUntil: 0 },
  };
};

// CLOSE_INFILTRATION handler — dismiss result / abandon run
const handleCloseInfiltration: ActionHandler<GameState, Extract<GameAction, { type: 'CLOSE_INFILTRATION' }>> = (state) => {
  if (!state.infiltration.run) return null;
  return { ...state, infiltration: { ...state.infiltration, run: null } };
};

// === Operation Mode (Plague-style world domination) ===

// START_OPERATION handler — begin a fresh run with race/difficulty/start region
const handleStartOperation: ActionHandler<GameState, Extract<GameAction, { type: 'START_OPERATION' }>> = (state, action) => {
  if (state.operation.active) return null;
  const { race, difficulty, startRegion } = action.payload;
  const run = createOperationRun(race, difficulty, startRegion);
  if (!run) return null;
  return { ...state, operation: run };
};

// OPERATION_TICK handler — advance the sim one tick (calc lives in systems)
const handleOperationTick: ActionHandler<GameState, Extract<GameAction, { type: 'OPERATION_TICK' }>> = (state) => {
  const next = advanceOperation(state.operation);
  if (!next) return null;
  return { ...state, operation: next };
};

// BUY_EVOLUTION handler — purchase an evolution node with DNA
const handleBuyEvolution: ActionHandler<GameState, Extract<GameAction, { type: 'BUY_EVOLUTION' }>> = (state, action) => {
  const { nodeId } = action.payload;
  if (!canBuyEvolution(state.operation, nodeId)) return null;
  const node = getEvoNode(nodeId);
  if (!node) return null;
  return {
    ...state,
    operation: {
      ...state.operation,
      dna: state.operation.dna - node.cost,
      boughtNodes: [...state.operation.boughtNodes, nodeId],
    },
  };
};

// COLLECT_DNA_BUBBLE handler — grab the DNA burst floating over the map
const handleCollectDnaBubble: ActionHandler<GameState, Extract<GameAction, { type: 'COLLECT_DNA_BUBBLE' }>> = (state) => {
  const bubble = state.operation.bubble;
  if (!bubble) return null;
  return {
    ...state,
    operation: { ...state.operation, dna: state.operation.dna + bubble.amount, bubble: null },
  };
};

// RESOLVE_OPERATION handler — acknowledge the win/lose report, return to picker
const handleResolveOperation: ActionHandler<GameState, Extract<GameAction, { type: 'RESOLVE_OPERATION' }>> = (state) => {
  if (!state.operation.outcome) return null;
  return { ...state, operation: blankOperation() };
};

// ABANDON_OPERATION handler — voluntarily give up an in-progress run
const handleAbandonOperation: ActionHandler<GameState, Extract<GameAction, { type: 'ABANDON_OPERATION' }>> = (state) => {
  if (!state.operation.active) return null;
  return { ...state, operation: blankOperation() };
};

// RESET handler
const handleReset: ActionHandler<GameState, Extract<GameAction, { type: 'RESET' }>> = () => {
  return createInitialState();
};

/** All action handlers map */
export const actionHandlers = {
  TAP: handleTap,
  TICK: handleTick,
  BUY_PRODUCER: handleBuyProducer,
  BUY_CLICK_UPGRADE: handleBuyClickUpgrade,
  BUY_SPECIAL_UPGRADE: handleBuySpecialUpgrade,
  PRESTIGE_1: handlePrestige1,
  PRESTIGE_2: handlePrestige2,
  BUY_PRESTIGE_UPGRADE: handleBuyPrestigeUpgrade,
  BUY_PRESTIGE2_UPGRADE: handleBuyPrestige2Upgrade,
  CLAIM_OFFLINE_REWARD: handleClaimOfflineReward,
  TOUCH_GOLDEN_EVENT: handleTouchGoldenEvent,
  SPAWN_GOLDEN_EVENT: handleSpawnGoldenEvent,
  CHECK_ACHIEVEMENTS: handleCheckAchievements,
  AD_WATCHED: handleAdWatched,
  USE_CLASSIFIED_DOC: handleUseDoc,
  ACTIVATE_BOOST: handleActivateBoost,
  CLEAN_EXPIRED_BOOSTS: handleCleanBoosts,
  ADD_IP: handleAddIp,
  ADD_DOCS: handleAddDocs,
  REFRESH_DAILY_MISSIONS: handleRefreshDailyMissions,
  UPDATE_MISSION_PROGRESS: handleUpdateMissionProgress,
  CLAIM_DAILY_MISSION: handleClaimDailyMission,
  CLAIM_ALL_MISSIONS_BONUS: handleClaimAllMissionsBonus,
  SET_ACTIVE_COUNTRY: handleSetActiveCountry,
  INVEST_CONQUEST: handleInvestConquest,
  BUILD_FACILITY: handleBuildFacility,
  UPGRADE_FACILITY: handleUpgradeFacility,
  UNLOCK_ENDING: handleUnlockEnding,
  PROMOTE_RANK: handlePromoteRank,
  SPAWN_BOSS: handleSpawnBoss,
  TAP_BOSS: handleTapBoss,
  CLAIM_BOSS_REWARD: handleClaimBossReward,
  BOSS_EXPIRED: handleBossExpired,
  AD_BOSS_DOUBLE_DAMAGE: handleAdBossDoubleDamage,
  AD_BOSS_REVIVE: handleAdBossRevive,
  AD_BOSS_REWARD_DOUBLE: handleAdBossRewardDouble,
  GRANT_AGENT: handleGrantAgent,
  EQUIP_AGENT: handleEquipAgent,
  UNEQUIP_AGENT: handleUnequipAgent,
  SPAWN_FACTION_EVENT: handleSpawnFactionEvent,
  HELP_FACTION_EVENT: handleHelpFactionEvent,
  IGNORE_FACTION_EVENT: handleIgnoreFactionEvent,
  AD_FACTION_EVENT_BONUS: handleAdFactionEventBonus,
  START_INFILTRATION: handleStartInfiltration,
  CHOOSE_INFILTRATION_OPTION: handleChooseInfiltration,
  ADVANCE_INFILTRATION: handleAdvanceInfiltration,
  RESOLVE_INFILTRATION: handleResolveInfiltration,
  CLOSE_INFILTRATION: handleCloseInfiltration,
  AD_INFILTRATION_RETRY: handleAdInfiltrationRetry,
  START_OPERATION: handleStartOperation,
  OPERATION_TICK: handleOperationTick,
  BUY_EVOLUTION: handleBuyEvolution,
  COLLECT_DNA_BUBBLE: handleCollectDnaBubble,
  RESOLVE_OPERATION: handleResolveOperation,
  ABANDON_OPERATION: handleAbandonOperation,
  RESET: handleReset,
} as const;

// Re-export utility functions for UI
export { getTotalCps, getTapPower, getComboTimeout, getCritChance, getCritMultiplier } from './systems/production.js';
export { calculateGcEarned, canPrestige, calculateCoresEarned, canPrestige2, getStartingIp, getOfflineCapHours, getOfflineEfficiency } from './systems/prestige.js';
export { calculateOfflineIp } from './systems/offline.js';
export { getProducerCost } from './data/producers.js';
export { COUNTRIES, getFacilityCost } from './data/conquest.js';
export { isCountryUnlocked, isAllConquered, getConqueredCount, getConquestCpsMult, getConquestTapMult, getInfiltrationPower } from './systems/conquest.js';
